import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import CropperJSWrapper from 'cropperjs-react';
// import CropperJSWrapper from '@components/common/cropper/wrapper'; // local version

import renderModal from '@components/show_page/modals/renderModal';
import { get as getAsset } from '@api/v4/assets';
import { get as getAttachment } from '@api/v4/attachments';
import { get as getPresets } from '@api/v4/resources/conversion_presets';
import CropDimensions from '@components/advanced_download/crop_dimensions';
import Options from '@components/advanced_download/options/main';
import Header from '@components/advanced_download/header';
import CropperFooter from '@components/advanced_download/cropper_footer';
import { track as trackLogRocket } from '@helpers/logRocket';
import { cleanExtension, defaultPresets, defaultCroppedData, templateCroppingPreset } from './helper';

import '@components/advanced_download/styles/main';

const AdvancedDownloadModal = ({ closeModal, lastVisited, resource, resetShowModal, lockedPreset, handleDownloadLink }) => {
  const [asset, setAsset] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [imageToThumbnailRatio, setImageToThumbnailRatio] = useState(1);

  const [availablePresets, setAvailablePresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [containerData, setContainerData] = useState(null);
  const [canvasData, setCanvasData] = useState(null);
  const [croppedData, setCroppedData] = useState(defaultCroppedData);
  const [zoomRatio, setZoomRatio] = useState(1);

  const [getter, setGetter] = useState({ action: '', func: () => {} });
  const [setter, setSetter] = useState({ action: '', data: {} });
  const [aspectRatio, setAspectRatio] = useState(null);
  const [fixedDimensions, setFixedDimensions] = useState(false);
  const [filetypes, setFiletypes] = useState(['']);
  const [selectedFiletype, setSelectedFiletype] = useState('');
  const [restrictOptionsType, setRestrictOptionsType] = useState(null);

  const getImageUrl = (response) => {
    if (response && response.attributes) {
      const { url, view_thumbnail_retina } = response.attributes;
      return view_thumbnail_retina || url || '';
    }
    return '';
  };

  const processAttachment = (response) => {
    setAttachment(response);
    setImageUrl(getImageUrl(response));

    const { conversion_options, extension, width } = response.attributes;
    const filetype = extension ? cleanExtension(extension) : 'PNG';
    const uniqueConversionOptions = [...new Set([filetype, ...conversion_options])];
    setFiletypes(uniqueConversionOptions);
    setSelectedFiletype(filetype);

    const detachedImage = new Image();
    detachedImage.addEventListener('load', (e) => {
      setImageToThumbnailRatio(width / e.target.naturalWidth);
    });
    detachedImage.src = getImageUrl(response);
  };

  useEffect(() => {
    trackLogRocket('cropping');
    const attachmentOptions = { params: { fields: 'view_thumbnail_retina,updated_at,extension,conversion_options' } };
    const assetOptions = { params: { include: 'section' } };
    const { assetKey, attachmentKey } = lastVisited;
    const { type: resourceType, key: resourceKey } = resource;
    getAttachment(attachmentKey, attachmentOptions).then(processAttachment);
    if (assetKey) { getAsset(assetKey, assetOptions).then(setAsset); }
    if (lockedPreset && lockedPreset.name === "Template Cropping") {
      const templatePreset = templateCroppingPreset(lockedPreset);
      setAvailablePresets([templatePreset]);
      setSelectedPreset({ label: templatePreset.name, value: templatePreset });
      setRestrictOptionsType(["Custom"]);
      setAspectRatio(templatePreset.aspectRatio);
    } else {
      getPresets({ resourceType, resourceKey })
        .then((response) => {
          if (response && response.length) {
            setAvailablePresets([...response, ...defaultPresets]);
          } else {
            setAvailablePresets([...defaultPresets]);
          }
        })
        .catch((err) => {
          console.log(err);
          setAvailablePresets([...defaultPresets]);
        });
    }
  }, [lockedPreset]);

  useEffect(() => {
    if (canvasData) {
      setZoomRatio(canvasData.width / canvasData.naturalWidth);
    }
  }, [canvasData]);

  useEffect(() => {
    setSetter({ action: 'setAspectRatio', data: { aspectRatio } });
  }, [aspectRatio]);

  const goBack = () => {
    if (lockedPreset && lockedPreset.name === "Template Cropping") {
      closeModal();
    } else {
      const sectionKey = asset.relationships.section.data.id;
      const [brandfolder, collection] = [BF.fx.brandfolder(), BF.fx.collection()];
      const urlBaseString = `${brandfolder.slug}/sections/${sectionKey}/ui/${asset.type}/${asset.id}`;
      const urlParamsString = collection.slug ? `?collection_slug=${collection.slug}` : "";
      const url = urlBaseString + urlParamsString;

      resetShowModal();
      BF.fullModal = true;
      BF.dialog.render(encodeURIComponent(asset.attributes.name), url, BF.handlerGroups.showAsset);
    }
  };

  const updateCanvasData = (newCanvasData) => {
    // only update if canvas moves
    if (newCanvasData && (newCanvasData.left !== canvasData.left || newCanvasData.top !== canvasData.top)) {
      setCanvasData(newCanvasData);
    }
  };

  const initializeCropper = () => {
    setGetter({ action: 'getCanvasData', func: setCanvasData });
    setGetter({ action: 'getContainerData', func: setContainerData });
    setGetter({ action: 'getData', func: setCroppedData });
  };

  const onZoom = () => {
    setGetter({ action: 'getCanvasData', func: setCanvasData });
  };

  const onCrop = (cropEvent) => {
    setGetter({ action: 'getCanvasData', func: updateCanvasData });
    setCroppedData(cropEvent.detail);
  };

  return (
    <section id="advanced-download-component" className="advanced-download-component">
      <Header
        attachment={attachment || undefined}
        closeModal={closeModal}
        goBack={goBack}
      />
      <div className="cropper-container">
        <CropperJSWrapper
          aspectRatio={(lockedPreset && lockedPreset.aspectRatio) ? lockedPreset.aspectRatio : NaN}
          imageUrl={imageUrl}
          onCrop={onCrop}
          onReady={initializeCropper}
          onZoom={onZoom}
          getter={getter}
          setter={setter}
        />
        <CropDimensions
          canvasData={canvasData}
          containerData={containerData}
          croppedData={croppedData}
          fixedDimensions={fixedDimensions}
          imageToThumbnailRatio={imageToThumbnailRatio}
          selectedPreset={selectedPreset}
          zoomRatio={zoomRatio}
        />
        <CropperFooter
          imageToThumbnailRatio={imageToThumbnailRatio}
          setSetter={setSetter}
          zoomRatio={zoomRatio}
        />
      </div>
      <Options
        aspectRatio={aspectRatio}
        attachment={attachment}
        availablePresets={availablePresets}
        croppedData={croppedData}
        filetypes={filetypes}
        fixedDimensions={fixedDimensions}
        imageToThumbnailRatio={imageToThumbnailRatio}
        selectedFiletype={selectedFiletype}
        selectedPreset={selectedPreset}
        setAspectRatio={setAspectRatio}
        setFixedDimensions={setFixedDimensions}
        setGetter={setGetter}
        setSetter={setSetter}
        setSelectedFiletype={setSelectedFiletype}
        setSelectedPreset={setSelectedPreset}
        zoomRatio={zoomRatio}
        restrictOptionsType={restrictOptionsType}
        handleDownloadLink={handleDownloadLink}
      />
    </section>
  );
};

AdvancedDownloadModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  lastVisited: PropTypes.shape({
    assetKey: PropTypes.string,
    attachmentKey: PropTypes.string,
  }).isRequired,
  resource: PropTypes.shape({
    type: PropTypes.string,
    key: PropTypes.string,
  }).isRequired,
  resetShowModal: PropTypes.func.isRequired,
  lockedPreset: PropTypes.shape({
    name: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    aspect_ratio: PropTypes.number
  }),
  handleDownloadLink: PropTypes.func,
};

AdvancedDownloadModal.defaultProps = {
  lockedPreset: null,
  handleDownloadLink: null,
};

const ModalComponent = renderModal(AdvancedDownloadModal, 'advanced-download-modal');
export default ModalComponent;
